import { FeatureModelType } from '../models/feature-model.type';
import { Feature } from 'ol';
import { GeoJSON, WKT } from 'ol/format.js';
import { FeatureModel, FeatureModelAttributes } from '@tailormap-viewer/api';
import { Circle, Geometry, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom.js';
import { fromCircle, fromExtent } from 'ol/geom/Polygon.js';
import { MapSizeHelper } from '../helpers/map-size.helper';
import { MapUnitEnum } from '../models/map-unit.enum';
import { GeometryTypeHelper } from './geometry-type.helper';
import { Projection } from 'ol/proj.js';
import { Feature as GeoJSONFeature } from 'geojson';
import { nanoid } from 'nanoid';
import { WriteOptions } from 'ol/format/Feature.js';
import { ProjectionsHelper } from './projections.helper';

export class FeatureHelper {

  private static wktFormatter = new WKT();
  private static geoJsonFormatter = new GeoJSON();

  /**
   * Transforms a GeoJSON geometry object to an ol.Geometry object; when the geometry is a point and a radius is given a circle is created.
   * @param geojsonGeometry The GeoJSON object to transform
   * @param radius The radius of the circle in case of a point
   * @param sourceProjection
   * @param mapProjection
   */
  public static fromGeoJSON(geojsonGeometry: object, radius?: number, sourceProjection?: string, mapProjection?: string): Geometry {
    if (/* geojsonGeometry['type'] === 'Point'  && */ radius) {
      const point = FeatureHelper.geoJsonFormatter.readGeometry(geojsonGeometry, {
        dataProjection: sourceProjection, featureProjection: mapProjection,
      }) as Point;
      return new Circle(point.getCoordinates(), radius);
    }

    return FeatureHelper.geoJsonFormatter.readGeometry(geojsonGeometry, {
      dataProjection: sourceProjection, featureProjection: mapProjection,
    });
  }

  /**
   * Transforms an ol.Geometry to a GeoJSON feature.
   * @param olGeometry The ol.Geometry object to transform
   * @param sourceProjection
   * @param mapProjection
   */
  public static toGeoJSON(olGeometry: Geometry, sourceProjection?: string, mapProjection?: string): GeoJSONFeature {
    if (olGeometry.getType() === 'Circle') {
      const circle = olGeometry as Circle;
      const feature = new Feature(new Point(circle.getCenter()));
      const geoJsonPoint = FeatureHelper.geoJsonFormatter.writeFeatureObject(feature, {
        dataProjection: sourceProjection, featureProjection: mapProjection,
      });
      if (!geoJsonPoint.properties) {
        geoJsonPoint.properties = {};
      }
      geoJsonPoint.properties['radius'] = circle.getRadius();
      return geoJsonPoint;
    }
    const feature = new Feature(olGeometry);
    return FeatureHelper.geoJsonFormatter.writeFeatureObject(feature, {
      dataProjection: sourceProjection, featureProjection: mapProjection,
    });
  }

  private static isFeatureModel(feature: FeatureModelType): feature is FeatureModel {
    return !!(feature as FeatureModel).__fid;
  }

  public static getFeatures(featureModel?: FeatureModelType | FeatureModelType[], mapProjection?: string): Feature<Geometry>[] {
    if (!featureModel) {
      return [];
    }

    const features = Array.isArray(featureModel)
      ? featureModel
      : [featureModel];

    return features.map(feature => {
      if (typeof feature === 'string') {
        try {
          const geometry = FeatureHelper.fromWKT(feature);
          return new Feature(geometry);
        } catch (e) {
          return null;
        }
      }
      if (FeatureHelper.isFeatureModel(feature)) {
        if (!feature.geometry) {
          return null;
        }
        return new Feature<Geometry>({
          __fid: feature.__fid,
          attributes: feature.attributes,
          geometry: FeatureHelper.fromWKT(feature.geometry, feature.crs, mapProjection),
        });
      }
      if (feature instanceof Geometry) {
        return new Feature<Geometry>({ geometry: feature });
      }
      return feature;
    }).filter((f: Feature<Geometry> | null): f is Feature<Geometry> => f !== null);
  }

  public static getFeatureModelForFeature<T extends FeatureModelAttributes>(
    feature: Feature<Geometry>,
    projection?: Projection,
  ): FeatureModel<T> | null {
    const geom = feature.getGeometry();
    if (geom) {
      return {
        __fid: feature.get('__fid') || feature.getId() || nanoid(),
        attributes: feature.get('attributes') || {},
        geometry: FeatureHelper.getWKT(geom, projection),
      };
    }
    return null;
  }

  public static getWKT(geometry: Geometry, projection?: Projection, linearizeCircle?: boolean): string {
    const writeOptions: WriteOptions = {};
    if (projection) {
      const units = projection.getUnits();
      writeOptions.decimals = MapSizeHelper.getCoordinatePrecision(units ? units.toLowerCase() as MapUnitEnum : MapUnitEnum.m);
    }

    if (GeometryTypeHelper.isCircleGeometry(geometry) && !linearizeCircle) {
      return FeatureHelper.writeCircleWKT(geometry, writeOptions.decimals);
    }
    const geom = GeometryTypeHelper.isCircleGeometry(geometry) ? fromCircle(geometry) : geometry;
    return FeatureHelper.wktFormatter.writeGeometry(geom, writeOptions);
  }

  private static writeCircleWKT(circle: Circle, decimals?: number): string {
    let xyr = [ circle.getCenter()[0], circle.getCenter()[1], circle.getRadius() ];
    if (decimals) {
      // Code from ol/format/Feature transformGeometryWithOptions()
      // Can't use directly on Circle Geometry because of radius
      const power = Math.pow(10, decimals);
      xyr = xyr.map(v => Math.round(v * power) / power);
    }
    return `CIRCLE(${xyr.join(' ')})`;
  }

  public static fromWKT(geometry: string, sourceProjection?: string, mapProjection?: string): Geometry {
    if (geometry.startsWith('CIRCLE')) {
      return FeatureHelper.readCircleWKT(geometry, sourceProjection, mapProjection);
    }
    if (!sourceProjection || !mapProjection || sourceProjection === mapProjection) {
      return FeatureHelper.wktFormatter.readGeometry(geometry);
    }
    return FeatureHelper.wktFormatter.readGeometry(geometry, {
      dataProjection: sourceProjection,
      featureProjection: mapProjection,
    });
  }

  private static readCircleWKT(geometry: string, sourceProjection?: string, mapProjection?: string) {
    const circleXY = geometry.replace('CIRCLE(', '').replace(')', '').split(' ');
    const center = [ parseFloat(circleXY[0]), parseFloat(circleXY[1]) ];
    const radius = parseFloat(circleXY[2]);
    const geom = new Circle(center, radius);
    if (!sourceProjection || !mapProjection || sourceProjection === mapProjection) {
      return geom;
    }
    return geom.transform(sourceProjection, mapProjection);
  }

  /**
   * Transforms a WKT geometry from one projection to another.
   *
   * @param wktGeom WKT of the geometry to transform
   * @param sourceProjection The projection of the geometry, eg. 'EPSG:28992'
   * @param targetProjection The projection to transform to, eg. 'EPSG:4326'
   */
  public static transformGeometry(wktGeom: string, sourceProjection: string, targetProjection: string): string {
    if (!sourceProjection || !targetProjection || sourceProjection === targetProjection) {
      return wktGeom;
    }
    const geom = FeatureHelper.wktFormatter.readGeometry(wktGeom, { dataProjection: sourceProjection });
    geom.transform(sourceProjection, targetProjection);
    return FeatureHelper.wktFormatter.writeGeometry(geom);
  }

  public static translateGeometryForDuplication(wktGeom: string, deltaX: number, deltaY: number): string {
    const geom = FeatureHelper.fromWKT(wktGeom);
    geom.translate(deltaX, deltaY);
    return FeatureHelper.getWKT(geom);
  }

  public static createRectangleAtPoint(pointWkt: string, width: number, height: number, projection?: string): string | null {
    const point = FeatureHelper.fromWKT(pointWkt);
    if (!(point instanceof Point)) {
      return null;
    }
    const coords = point.getFlatCoordinates();
    let projectedWidth = width;
    let projectedHeight = height;
    if (projection && ProjectionsHelper.needsSphericalMeasurements(projection)) {
      projectedWidth = MapSizeHelper.metersToProjectedUnitsAtPoint(coords, width, projection);
      projectedHeight = MapSizeHelper.metersToProjectedUnitsAtPoint(coords, height, projection);
    }
    return FeatureHelper.getWKT(fromExtent([
      coords[0] - projectedWidth / 2,
      coords[1] - projectedHeight / 2,
      coords[0] + projectedWidth / 2,
      coords[1] + projectedHeight / 2,
    ]));
  }

  public static createCircleAtPoint(pointWkt: string, radius: number, projection?: string) {
    const point = FeatureHelper.fromWKT(pointWkt);
    if (!(point instanceof Point)) {
      return null;
    }
    let projectedRadius = radius;
    if (projection && ProjectionsHelper.needsSphericalMeasurements(projection)) {
      projectedRadius = MapSizeHelper.metersToProjectedUnitsAtPoint(point.getCoordinates(), radius, projection);
    }
    const circle = new Circle(point.getCoordinates(), projectedRadius);
    return FeatureHelper.getWKT(circle);
  }

  public static appendMultiGeometryWKT(currentWkt: string | null, newGeometryWkt: string) {
    const newGeom = FeatureHelper.fromWKT(newGeometryWkt);
    if (!currentWkt) {
      return newGeom;
    }
    const currentMultiGeom = this.ensureMultiGeometry(FeatureHelper.fromWKT(currentWkt));

    if (currentMultiGeom instanceof MultiPoint && newGeom instanceof Point) {
      currentMultiGeom.appendPoint(newGeom);
    } else if (currentMultiGeom instanceof MultiLineString && newGeom instanceof LineString) {
      currentMultiGeom.appendLineString(newGeom);
    } else if (currentMultiGeom instanceof MultiPolygon && newGeom instanceof Polygon) {
      currentMultiGeom.appendPolygon(newGeom);
    } else {
      // In case of incompatible geometry types, just return the new geometry
      // TODO, maybe create a GeometryCollection?
      return newGeom;
    }
    return currentMultiGeom;
  }

  private static ensureMultiGeometry(geometry: Geometry) {
    if (geometry instanceof MultiPoint || geometry instanceof MultiLineString || geometry instanceof MultiPolygon) {
      return geometry;
    }
    if (geometry instanceof Point) {
      const multiPoint = new MultiPoint([]);
      multiPoint.appendPoint(geometry);
      return multiPoint;
    }
    if (geometry instanceof LineString) {
      const multiLineString = new MultiLineString([]);
      multiLineString.appendLineString(geometry);
      return multiLineString;
    }
    if (geometry instanceof Polygon) {
      const multiPolygon = new MultiPolygon([]);
      multiPolygon.appendPolygon(geometry);
      return multiPolygon;
    }
    throw new Error('Unsupported geometry type');
  }
}
